import { useEffect, useState } from 'react';
import useToastContext from '@contexts/ToastContext/hook';
import { parseFilters } from '@utils/parseFilters';
import { findPaymentConversion } from '@api/reports';
import {
  PaymentConversionResponse,
  PaymentConversionTotals,
} from '@interfaces/reports/paymentConversion';

export type PaymentConversionFiltersInput = {
  botConfigIds: number[];
  startDate: Date;
  endDate: Date;
  previousPeriodStartDate: Date;
  previousPeriodEndDate: Date;
  clientTz: string;
  /**
   * O papel pedido como ESTREITAMENTO, e opcional desde a #360.
   *
   * Ausente é o modo "Tudo o que eu alcanço": o recorte sai do `scopeByProduct` que a `platform-api`
   * resolve, e é o único que fala de acesso por grant.
   */
  userRoles?: string[];
  /** Carried only so the drill-down link can hand the period label over; not part of the request. */
  periodRange?: string;
};

export const emptyTotals: PaymentConversionTotals = {
  createdCount: 0,
  paidCount: 0,
  paidRate: 0,
  createdGrossValue: 0,
  paidGrossValue: 0,
  averageMinutesToPay: null,
  paidWithin24hCount: 0,
  paidWithin24hRate: 0,
};

export const emptyResponse: PaymentConversionResponse = {
  rows: [],
  totals: emptyTotals,
};

/**
 * Loads the period and its comparison.
 *
 * Shared by the dashboard summary and the detail screen so both fetch identically - one request pair,
 * one set of guards - instead of each growing its own copy of this.
 */
export function usePaymentConversion(filters: PaymentConversionFiltersInput) {
  const toast = useToastContext();
  const [data, setData] = useState<PaymentConversionResponse>(emptyResponse);
  /**
   * Null means the comparison could not be loaded - distinct from a previous period that WAS loaded and
   * happens to be empty. Substituting zeros made ProfitIndicator read current-versus-zero as growth from
   * nothing and paint a green "novo", so a transient failure claimed a first-ever result.
   */
  const [previous, setPrevious] = useState<PaymentConversionResponse | null>(
    emptyResponse,
  );
  const [isLoading, setIsLoading] = useState(false);
  /**
   * The CURRENT period's request having failed, as opposed to having answered with nothing.
   *
   * Only the current one: a failed comparison already degrades into no badge, which is honest. The
   * current one falling back to the empty payload is what made a broken request draw 0% paid, indistinguishable
   * from a period where nobody paid.
   */
  const [hasFailed, setHasFailed] = useState(false);
  /** A chave dos filtros a que `data` responde. Ver a nota de `isStale`, no fim. */
  const [respondidoPara, setRespondidoPara] = useState<string | null>(null);
  const chaveAtual = JSON.stringify(filters ?? null);

  useEffect(() => {
    // Guards against a superseded request. Changing communities or period fires a new pair while the old
    // one is still in flight, and without this the slower response wins whichever it is - including its
    // finally clearing the loading state of a request that has not finished.
    let active = true;

    if (!filters.botConfigIds?.length) {
      setData(emptyResponse);
      setPrevious(emptyResponse);
      setHasFailed(false);
      // Clears the spinner explicitly. Deselecting every community while a request is in flight
      // invalidates that request, so its finally no longer does this - and without it the card would
      // spin forever instead of showing the empty state.
      setIsLoading(false);
      // Sem comunidade não há busca, e a resposta vazia é a resposta DESTE recorte.
      setRespondidoPara(chaveAtual);
      return () => {
        active = false;
      };
    }

    const parsed = parseFilters(filters);
    const request = {
      botConfigIds: parsed.botConfigIds,
      clientTz: parsed.clientTz,
      userRoles: parsed.userRoles,
      timeAggregationPeriod: 'DAY' as const,
    };

    setIsLoading(true);
    // Settled, not all: the comparison failing must not throw away a current period that came back fine.
    Promise.allSettled([
      findPaymentConversion({
        ...request,
        startDate: parsed.startDate,
        endDate: parsed.endDate,
      }),
      findPaymentConversion({
        ...request,
        startDate: parsed.previousPeriodStartDate,
        endDate: parsed.previousPeriodEndDate,
      }),
    ])
      .then(([current, prior]) => {
        if (!active) return;

        setHasFailed(current.status === 'rejected');
        setData(
          current.status === 'fulfilled'
            ? current.value.data ?? emptyResponse
            : emptyResponse,
        );
        // Null, not zeros: see the state declaration.
        setPrevious(
          prior.status === 'fulfilled'
            ? prior.value.data ?? emptyResponse
            : null,
        );

        // Only the current period is worth interrupting the producer about. A failed comparison degrades
        // into no badge at all.
        if (current.status === 'rejected')
          toast.handleError(
            current.reason,
            'Não foi possível carregar os registros',
          );
      })
      .finally(() => {
        if (!active) return;
        setIsLoading(false);
        // A chave entra junto do commit do dado, e como estado. Ver a nota em `useBotFunnel`.
        setRespondidoPara(chaveAtual);
      });

    return () => {
      active = false;
    };
  }, [filters]);

  /**
   * Se `data` ainda é a resposta de um recorte anterior. Ver a nota equivalente em `useBotFunnel`:
   * `isLoading` só vira `true` dentro do efeito, então o render em que o filtro muda vê `false` com o
   * dado velho.
   */
  const isStale = respondidoPara !== chaveAtual;

  return {
    data,
    totals: data.totals ?? emptyTotals,
    /** Undefined when the comparison is unavailable, so callers omit their indicators. */
    priorTotals: previous ? previous.totals ?? emptyTotals : undefined,
    isLoading,
    isStale,
    hasFailed,
  };
}
